import type { ReactNode } from "react";
import { Link } from "react-router";

import { useBreakdown, type BreakdownRow } from "../../api/hooks.ts";
import { mergeRows } from "../../domain/breakdown.ts";
import { formatPercent, formatR, toneOf } from "../../domain/format.ts";
import { Bars, type BarItem } from "../../ui/Bars.tsx";
import { HalfGauge } from "../../ui/HalfGauge.tsx";
import { Icon, type IconName } from "../../ui/Icon.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { usePermissions } from "../auth/permissions.ts";

/**
 * Aprendizaje: las mismas cifras de Metricas, leidas como lecciones.
 *
 * SPEC 11 dice cuales son las tres preguntas que suelen explicar un mes malo, y
 * esta pantalla no hace nada mas que contestarlas:
 *
 *   1. Cuando siento X, como me va         -> dimension `emotion`
 *   2. Cuando cometo este error, cuanto me cuesta -> dimension `mistake`
 *   3. Opero mejor cuando cumplo el plan al 100 % -> dimension `adherenceBand`
 *
 * La respuesta va como FRASE con su cifra al lado, no como otra tabla. Una tabla
 * mas obliga al usuario a sacar la conclusion; aqui la conclusion ya esta
 * escrita y la cifra esta al lado para que pueda no creersela.
 *
 * Sin rango: una leccion se saca del historial entero. Quien quiera acotar el
 * periodo tiene Metricas.
 */
export function Learning(): ReactNode {
  const { canWriteTrades } = usePermissions();
  const emotions = useBreakdown("emotion");
  const mistakes = useBreakdown("mistake");
  const bands = useBreakdown("adherenceBand");

  const pending = emotions.isPending || mistakes.isPending || bands.isPending;
  const error = emotions.error ?? mistakes.error ?? bands.error;

  const emotionRows = emotions.data?.rows ?? [];
  const mistakeRows = mistakes.data?.rows ?? [];
  const bandRows = bands.data?.rows ?? [];

  const empty =
    emotionRows.length === 0 && mistakeRows.length === 0 && bandRows.length === 0;

  return (
    <StatusBlock
      loading={pending}
      error={error}
      empty={empty}
      emptyIcon="book"
      emptyTitle="Todavia no hay lecciones que sacar."
      emptyText="Estas tres preguntas se contestan con operaciones cerradas y revisadas. Cierra la primera y vuelve."
      emptyAction={
        canWriteTrades ? (
          <Link className="btn primary" to="/trades/new">
            <Icon name="plus" />
            Registrar una operacion
          </Link>
        ) : undefined
      }
      onRetry={() => {
        void emotions.refetch();
        void mistakes.refetch();
        void bands.refetch();
      }}
    >
      <div className="sec-h">
        <div>
          <h2>Tres preguntas</h2>
          <p>
            Las que suelen explicar un mes malo. Las contestan tus propias operaciones, no una
            impresion del domingo.
          </p>
        </div>
      </div>

      <Headline mistakes={mistakeRows} emotions={emotionRows} />

      <div className="grid">
        <section className="panel col-6">
          <div className="panel-h">
            <div>
              <h3>Cuando siento esto, me va asi</h3>
              <p>Con que emocion entraste, y lo que dejo cada operacion</p>
            </div>
          </div>
          <div className="panel-b">
            <Emotions rows={emotionRows} />
            <CollectionNote note={emotions.data?.note ?? null} rows={emotionRows} />
          </div>
        </section>

        <section className="panel col-6">
          <div className="panel-h">
            <div>
              <h3>Cuando cometo este error, me cuesta esto</h3>
              <p>El precio de cada error, en R y no en anecdotas</p>
            </div>
          </div>
          <div className="panel-b">
            <Mistakes rows={mistakeRows} />
            <CollectionNote note={mistakes.data?.note ?? null} rows={mistakeRows} />
          </div>
        </section>

        <section className="panel col-12">
          <div className="panel-h">
            <div>
              <h3>Opero mejor cuando cumplo el plan al 100 %</h3>
              <p>La pregunta que separa la habilidad del resultado</p>
            </div>
          </div>
          <div className="panel-b">
            <Adherence rows={bandRows} splittable={bands.data?.note === null} />
          </div>
        </section>
      </div>
    </StatusBlock>
  );
}

/* ========================================================================== */
/* La frase con su cifra al lado                                              */
/* ========================================================================== */

function Finding(props: {
  tone: "good" | "bad" | "info" | "star";
  icon: IconName;
  headline: string;
  support: string;
  figure: string;
  figureTone?: string | undefined;
}): ReactNode {
  return (
    <div className={`insight ${props.tone}`}>
      <div className="i-ic">
        <Icon name={props.icon} />
      </div>
      <div className="i-body">
        <b>{props.headline}</b>
        <p>{props.support}</p>
      </div>
      <b className={`i-fig num ${props.figureTone ?? "muted"}`}>{props.figure}</b>
    </div>
  );
}

/**
 * El aviso de muestra corta.
 *
 * Con tres operaciones no hay leccion, hay ruido. Decirlo al lado de la cifra es
 * mas honesto que esconder la fila: el usuario ve el dato y ve lo que pesa.
 */
const ENOUGH = 5;

function sample(row: BreakdownRow): string {
  const base = `${row.trades} ${row.trades === 1 ? "operacion" : "operaciones"}, ${row.rated} con R`;

  return row.trades < ENOUGH ? `${base} · todavia son pocas para llamarlo senal` : base;
}

/* ========================================================================== */
/* 1. Emociones                                                               */
/* ========================================================================== */

function Emotions(props: { rows: readonly BreakdownRow[] }): ReactNode {
  const rows = withData(props.rows);

  if (rows.length === 0) {
    return <Nothing text="Marca la emocion con la que entraste al revisar una operacion y esta pregunta se contesta sola." />;
  }

  /* Aqui SI se ordena por resultado, al reves que en Metricas: la pregunta no es
     "como se reparten mis emociones" sino "cual me sale cara", y eso es un
     ranking. Las que no tienen R se van al final. */
  const ranked = [...rows].sort(
    (left, right) => (right.expectancyR ?? -Infinity) - (left.expectancyR ?? -Infinity),
  );

  return (
    <>
      {ranked.map((row) => (
        <Finding
          key={row.label}
          tone={moodOf(row.expectancyR)}
          icon="smile"
          headline={`Cuando entras con ${row.label.toLowerCase()}, cada operacion te deja`}
          support={`${sample(row)} · acierto ${formatPercent(row.winRate)} · ${formatR(row.totalR)} en total`}
          figure={formatR(row.expectancyR)}
          figureTone={toneOf(row.expectancyR)}
        />
      ))}
    </>
  );
}

/* ========================================================================== */
/* 2. Errores                                                                 */
/* ========================================================================== */

function Mistakes(props: { rows: readonly BreakdownRow[] }): ReactNode {
  const rows = withData(props.rows);

  if (rows.length === 0) {
    return <Nothing text="Marca los errores al revisar una operacion y aqui veras lo que cuesta cada uno." />;
  }

  /* Lo que mas duele, primero. Es el orden en el que se lee una factura. */
  const ranked = [...rows].sort((left, right) => left.totalR - right.totalR);

  return (
    <>
      {ranked.map((row) => {
        /* La fila sin clave del catalogo es el cajon de "sin error marcado", y
           es la comparacion que da sentido a las otras: si operar sin errores
           marcados tampoco paga, el problema no son los errores. */
        const baseline = row.key === null;
        const average = row.rated === 0 ? null : row.totalR / row.rated;

        return (
          <Finding
            key={row.label}
            tone={baseline ? "info" : row.totalR < 0 ? "bad" : "good"}
            icon={baseline ? "check" : "flame"}
            headline={
              baseline
                ? "Cuando no marcaste ningun error, sumaste"
                : `Cuando cometes ${row.label.toLowerCase()}, te ha costado`
            }
            support={`${sample(row)} · ${formatR(average)} de media · acierto ${formatPercent(row.winRate)}`}
            figure={formatR(row.totalR)}
            figureTone={toneOf(row.totalR)}
          />
        );
      })}
    </>
  );
}

/* ========================================================================== */
/* 3. Adherencia al plan                                                      */
/* ========================================================================== */

/*
 * Los tramos los nombra el SERVIDOR (`BreakdownLabels.AdherenceBand`), asi que
 * reconocerlos por su etiqueta es lo unico que se puede hacer desde aqui. Es
 * presentacion y no aritmetica: si un dia cambian esos textos, esta pantalla
 * pierde el titular y ensena los tramos tal cual, que es degradar bien. Ninguna
 * cifra sale mal por esto.
 */
function isPercentBand(row: BreakdownRow): boolean {
  return row.label.includes("%");
}

function isBreachBand(row: BreakdownRow): boolean {
  return row.label.toLowerCase().includes("innegociable");
}

function Adherence(props: { rows: readonly BreakdownRow[]; splittable: boolean | undefined }): ReactNode {
  const { rows } = props;

  if (rows.length === 0) {
    return <Nothing text="Revisa una operacion contra las reglas de su plan y esta pregunta empieza a tener respuesta." />;
  }

  const percent = rows.filter(isPercentBand);
  const breach = rows.find(isBreachBand) ?? null;
  const full = percent.find((row) => row.label.startsWith("100")) ?? null;
  const partial = percent.filter((row) => row !== full);

  /* `mergeRows` solo vale en dimensiones que no son de coleccion: el tramo de
     adherencia mete cada operacion en uno y solo uno, asi que sumar tramos suma
     operaciones distintas. Si algun dia dejara de ser asi (`note` no nulo), no
     se junta nada y se ensenan los tramos sueltos. */
  const rest =
    props.splittable === true && partial.length > 0 ? mergeRows("El plan a medias", partial) : null;
  const reviewed =
    props.splittable === true
      ? mergeRows("Revisadas", breach === null ? percent : [...percent, breach])
      : null;

  const bars: BarItem[] = rows.map((row) => ({
    label: row.label,
    value: row.expectancyR ?? 0,
    display: formatR(row.expectancyR),
    /* El ambar es de la regla innegociable rota y de nada mas (ADR 0003): mide
       disciplina, no dinero. Los demas tramos se pintan por el signo del R. */
    color: isBreachBand(row) ? "var(--warn)" : (row.expectancyR ?? 0) >= 0 ? "var(--up)" : "var(--down)",
    tone: toneOf(row.expectancyR),
    title: `${row.label}: ${row.trades} operaciones, ${formatR(row.totalR)} en total`,
  }));

  return (
    <div className="answer">
      <div className="answer-fig">
        <HalfGauge
          ratio={
            full === null || reviewed === null || reviewed.trades === 0
              ? null
              : full.trades / reviewed.trades
          }
          caption="de tus revisadas, con el plan entero"
        />
      </div>

      <div className="answer-body">
        {full !== null && rest !== null && full.rated > 0 && rest.rated > 0 ? (
          <Finding
            tone={(full.expectancyR ?? 0) > (rest.expectancyR ?? 0) ? "good" : "bad"}
            icon="check"
            headline={
              (full.expectancyR ?? 0) > (rest.expectancyR ?? 0)
                ? "Si. Cumplir el plan entero te sale mejor, y esta es la diferencia"
                : "No. Cumplir el plan entero no te esta saliendo mejor, por ahora"
            }
            support={
              `Con el plan al 100 %: ${formatR(full.expectancyR)} por operacion en ${full.rated}. ` +
              `Con el plan a medias: ${formatR(rest.expectancyR)} en ${rest.rated}.` +
              (breach !== null && breach.trades > 0
                ? " Las que rompieron una innegociable van aparte, abajo."
                : "")
            }
            figure={formatR((full.expectancyR ?? 0) - (rest.expectancyR ?? 0))}
            figureTone={toneOf((full.expectancyR ?? 0) - (rest.expectancyR ?? 0))}
          />
        ) : (
          <Finding
            tone="info"
            icon="clock"
            headline="Todavia no se puede contestar"
            support="Hacen falta operaciones revisadas en los dos lados: las que cumplieron el plan entero y las que no."
            figure="—"
          />
        )}

        {breach !== null && breach.trades > 0 && (
          <Finding
            tone="star"
            icon="lock"
            headline={`Rompiste una regla innegociable en ${breach.trades} ${breach.trades === 1 ? "operacion" : "operaciones"}`}
            support="Van aparte del porcentaje a proposito: nueve de diez reglas cumplidas es un 90 %, pero si la que falla es la innegociable no es una operacion de 90 %."
            figure={formatR(breach.totalR)}
            figureTone={toneOf(breach.totalR)}
          />
        )}

        <div style={{ marginTop: 14 }}>
          <Bars items={bars} wide />
        </div>
        <p className="hint" style={{ marginTop: 10 }}>
          Expectativa por tramo: lo que deja de media una operacion de cada uno.
        </p>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Piezas compartidas                                                         */
/* ========================================================================== */

function Headline(props: {
  mistakes: readonly BreakdownRow[];
  emotions: readonly BreakdownRow[];
}): ReactNode {
  /* El titular es la factura mas cara: el error marcado que mas R se ha llevado.
     Si no hay errores marcados, la emocion con peor expectativa. */
  const worstMistake = props.mistakes
    .filter((row) => row.key !== null && row.totalR < 0)
    .reduce<BreakdownRow | null>(
      (worst, row) => (worst === null || row.totalR < worst.totalR ? row : worst),
      null,
    );

  const worstEmotion = props.emotions
    .filter((row) => row.expectancyR !== null && row.expectancyR < 0)
    .reduce<BreakdownRow | null>(
      (worst, row) =>
        worst === null || (row.expectancyR ?? 0) < (worst.expectancyR ?? 0) ? row : worst,
      null,
    );

  if (worstMistake !== null) {
    return (
      <div className="week-lesson">
        <div className="big">
          <Icon name="flame" />
        </div>
        <div>
          <h3>Lo que mas te ha costado</h3>
          <q>
            {worstMistake.label}: {formatR(worstMistake.totalR)} en {worstMistake.trades}{" "}
            {worstMistake.trades === 1 ? "operacion" : "operaciones"}.
          </q>
          <p>
            No es una impresion del domingo: es la suma de tus propias operaciones. Reelelo antes de
            abrir la proxima.
          </p>
        </div>
      </div>
    );
  }

  if (worstEmotion !== null) {
    return (
      <div className="week-lesson">
        <div className="big">
          <Icon name="smile" />
        </div>
        <div>
          <h3>Con lo que peor entras</h3>
          <q>
            Entrando con {worstEmotion.label.toLowerCase()}, cada operacion te deja{" "}
            {formatR(worstEmotion.expectancyR)}.
          </q>
          <p>
            Ningun error marcado te sale mas caro que esto. Mirala antes de la proxima entrada.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * El `note` del contrato, pintado tambien aqui.
 *
 * Solo cuando hay filas: avisar de que las filas se solapan en un panel que no
 * tiene ninguna es ruido, y el ruido ensena al usuario a no leer los avisos.
 */
function CollectionNote(props: { note: string | null; rows: readonly BreakdownRow[] }): ReactNode {
  if (props.note === null || withData(props.rows).length === 0) return null;

  return (
    <p className="hint" style={{ marginTop: 10 }}>
      {props.note}
    </p>
  );
}

/** Las filas que no tienen ninguna operacion detras no dicen nada: fuera. */
function withData(rows: readonly BreakdownRow[]): readonly BreakdownRow[] {
  return rows.filter((row) => row.trades > 0);
}

function Nothing(props: { text: string }): ReactNode {
  return <p className="hint">{props.text}</p>;
}

/** Verde, rojo o neutro segun la expectativa. El vacio no es un cero. */
function moodOf(value: number | null): "good" | "bad" | "info" {
  if (value === null || value === 0) return "info";

  return value > 0 ? "good" : "bad";
}
